import { useCallback, useMemo } from "react";

import { UserMultiselectDropdown } from "@/domain/user/components/user-multiselect-dropdown";
import { useSmartUserList } from "@/domain/user/hooks/api/use-smart-user-list";
import type { User } from "@/domain/user/models/user-model";

import { SelectFieldProps } from "./select-field-props";
import { SelectTriggerButton } from "./select-trigger-button";

export function UserSelectField({ workspaceId, value, onChange, disabled }: SelectFieldProps) {
  const { userList } = useSmartUserList({ workspaceId });

  const activeUserList = useMemo(
    () =>
      userList.map(
        (u): User => ({
          id: u.id,
          email: u.email,
          fullname: u.fullname,
          username: u.username,
          profilePicture: u.profilePicture,
        }),
      ),
    [userList],
  );

  const selectedIdList = useMemo(() => (value ? value.split(",").filter(Boolean) : []), [value]);

  const selectedUserList = useMemo(
    () => activeUserList.filter((u) => selectedIdList.includes(u.id)),
    [activeUserList, selectedIdList],
  );

  const displayValue = useMemo(
    () => selectedUserList.map((u) => u.fullname ?? u.email).join(", "),
    [selectedUserList],
  );

  const handleValueChange = useCallback(
    (newUserList: User[]) => {
      onChange(newUserList.map((u) => u.id).join(","));
    },
    [onChange],
  );

  return (
    <UserMultiselectDropdown
      activeUserList={activeUserList}
      value={selectedUserList}
      onValueChange={handleValueChange}
      isReadOnly={disabled}
      asChild
    >
      <SelectTriggerButton value={displayValue} disabled={disabled} />
    </UserMultiselectDropdown>
  );
}
